import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import useInView from '../hooks/useInView';
import SEO from '../components/SEO';

const services = [
    {
        category: 'New Construction',
        items: [
            { name: 'Custom Home Builds', desc: 'Complete new home construction from concept to move-in, tailored to your vision and lifestyle.' },
            { name: 'Foundations & Formwork', desc: 'Precision concrete foundations and formwork engineered for the terrain of the Sea to Sky corridor.' },
            { name: 'Framing', desc: 'Structural framing built to code with quality lumber and meticulous attention to detail.' },
            { name: 'Siding & Exteriors', desc: 'Weather-resistant exterior cladding and siding installed to protect and define your home.' },
        ],
    },
    {
        category: 'Interior & Finishing',
        items: [
            { name: 'Interior Finishing', desc: 'Trim, cabinetry, doors, and all the details that bring a space to life.' },
            { name: 'Flooring', desc: 'Hardwood, tile, engineered flooring — installed with care for a flawless finish.' },
        ],
    },
    {
        category: 'Renovations & Restoration',
        items: [
            { name: 'Renovations', desc: 'Thoughtful renovations that modernize your space while respecting the original character.' },
            { name: 'Restoration Work', desc: 'Bringing aging structures back to life with careful restoration and structural repair.' },
            { name: 'Abatement', desc: 'Safe removal and remediation of hazardous materials to current regulatory standards.' },
        ],
    },
    {
        category: 'Commercial & Specialty',
        items: [
            { name: 'Commercial Upgrades & Maintenance', desc: 'Tenant improvements, facility upgrades, and ongoing maintenance for commercial properties.' },
            { name: 'Pools', desc: 'Custom pool construction and installation — from excavation to finishing.' },
        ],
    },
];

const Services = () => {
    const [introRef, introVisible] = useInView();

    return (
        <div>
            <SEO
                title="Our Services"
                description="Licensed home builder offering foundations, framing, siding, finishing, flooring, renovations, pools, restoration, and commercial construction in Squamish, Whistler, and Pemberton."
                keywords="construction services Squamish, framing contractor Whistler, renovation Pemberton, home builder services Sea to Sky, foundations, siding, flooring, pools, commercial contractor"
            />
            <PageBanner
                image="images/Crumpit_woods_Wilson.jpeg"
                subtitle="What We Do"
                title="Our Services"
                position="bg-[center_55%]"
            />

            {/* Intro */}
            <section ref={introRef} className="section max-w-3xl mx-auto text-center">
                <p className={`font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-4
                    transition-all duration-700 ${introVisible ? 'opacity-100' : 'opacity-0'}`}>
                    Licensed Home Builder
                </p>
                <h2 className={`font-serif text-4xl md:text-5xl font-light text-stone-800 mb-4 leading-tight
                    transition-all duration-700 delay-100 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    From Foundations to Finishing
                </h2>
                <div className={`divider transition-all duration-700 delay-200 ${introVisible ? 'opacity-100' : 'opacity-0'}`} />
                <p className={`font-sans text-stone-500 text-base leading-relaxed mt-6
                    transition-all duration-700 delay-300 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    As a licensed home builder with over 20 years of experience, Kia Kaha offers
                    a comprehensive range of construction services. Whether it's a ground-up custom
                    home, a renovation, or a commercial project, we bring the same standard of
                    craftsmanship to every job.
                </p>
            </section>

            {/* Service categories */}
            <section className="px-6 md:px-12 lg:px-24 pb-24">
                <div className="max-w-6xl mx-auto space-y-20">
                    {services.map((group) => (
                        <ServiceGroup key={group.category} group={group} />
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="section bg-stone-50 text-center">
                <div className="max-w-2xl mx-auto">
                    <h2 className="font-serif text-3xl md:text-4xl font-light text-stone-800 mb-4">
                        Ready to Start Your Project?
                    </h2>
                    <div className="divider" />
                    <p className="font-sans text-stone-500 text-base leading-relaxed mt-6 mb-10">
                        Every project begins with a conversation. Tell us about your vision and
                        we'll outline how we can bring it to life.
                    </p>
                    <Link to="/contact" className="btn-jade">Get In Touch</Link>
                </div>
            </section>
        </div>
    );
};

const ServiceGroup = ({ group }) => {
    const [ref, visible] = useInView();

    return (
        <div ref={ref}>
            <div className={`mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                <p className="font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-2">
                    {group.category}
                </p>
                <div className="w-8 h-px bg-jade-400" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {group.items.map((item, i) => (
                    <div
                        key={item.name}
                        className={`border-l-2 border-stone-200 pl-6 transition-all duration-700
                            ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                        style={{ transitionDelay: `${150 + i * 100}ms` }}
                    >
                        <h3 className="font-serif text-xl font-light text-stone-800 mb-2">
                            {item.name}
                        </h3>
                        <p className="font-sans text-sm text-stone-500 leading-relaxed">
                            {item.desc}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Services;
